import React, { useState, useEffect, useRef } from 'react';
import HealthTable from './HealthTable';
import AircraftRow from './AircraftRow';
import { getFaseFleet } from '../../faseMonitor/service';
import { updateTheme } from '../../faseMonitor/actions';
import { Toggle } from './Toggle';
import { Spare } from './Spare';
import { isUnauthorizedError } from '../../faseMonitor/auth-service';

interface FleetPageProps {
  customerCode?: string;
  filterHealth?: string | null;
}

interface FleetState {
  aircraft: any[];
  totalStats: {
    aircraft: { ok: number; halting: number; failing: number };
    boxes: { ok: number; halting: number; failing: number };
  };
  spareBoxesWithHealth: any[];
  loading: boolean;
  loadError: string | null;
}

const initialFleetState: FleetState = {
  aircraft: [],
  totalStats: { aircraft: { ok: 0, halting: 0, failing: 0 }, boxes: { ok: 0, halting: 0, failing: 0 } },
  spareBoxesWithHealth: [],
  loading: true,
  loadError: null,
};

function FleetPage({ customerCode, filterHealth }: FleetPageProps) {
  const loadGenerationRef = useRef(0);
  const [state, setState] = useState(initialFleetState);

  useEffect(() => {
    let isCancelled = false;
    const loadId = ++loadGenerationRef.current;
    setState((prev) => ({ ...prev, loading: true, aircraft: [], loadError: null }));

    async function loadFleet() {
      try {
        const response = (await getFaseFleet(customerCode)) || {};
        if (isCancelled || loadId !== loadGenerationRef.current) {
          return;
        }
        const {
          aircraft = [],
          totalStats = { aircraft: { ok: 0, halting: 0, failing: 0 }, boxes: { ok: 0, halting: 0, failing: 0 } },
          spareBoxesWithHealth = [],
        } = response;
        setState({ aircraft, totalStats, spareBoxesWithHealth, loading: false, loadError: null });
        updateTheme(customerCode, loadId);
      } catch (error: any) {
        if (isCancelled || loadId !== loadGenerationRef.current) {
          return;
        }
        setState((prev) => ({
          ...prev,
          loading: false,
          loadError: error && error.message ? error.message : String(error),
        }));
      }
    }

    loadFleet();

    return () => {
      isCancelled = true;
    };
  }, [customerCode]);

  const { totalStats, aircraft, spareBoxesWithHealth, loading, loadError } = state;

  if (loading) {
    return <div className="fase-monitor__status">Loading fleet data…</div>;
  }

  if (loadError) {
    const airlineLabel = customerCode ? ` to airline ${customerCode.toUpperCase()}` : '';
    const isUnauthorized = isUnauthorizedError(loadError);
    const message = isUnauthorized
      ? `You do not have access${airlineLabel}.`
      : 'Something went wrong. Please try again.';
    return <div className="fase-monitor__status fase-monitor__status--error">{message}</div>;
  }

  const aircraftRows = aircraft
    .filter((ac) => {
      return !filterHealth || ac.health === filterHealth;
    })
    .map((ac) => {
      return <AircraftRow key={ac.id} {...ac} />;
    });

  const aircraftContent =
    aircraftRows.length > 0 ? (
      <div className="fase-monitor__aircraft-list">{aircraftRows}</div>
    ) : (
      <div className="fase-monitor__empty">
        {filterHealth && aircraft.length > 0 ? 'No aircraft match the selected health filter' : 'No aircraft assigned'}
      </div>
    );

  return (
    <div className="fase-monitor__page">
      <div className="fase-monitor__stats">
        <HealthTable title="FASE Aircraft" {...totalStats.aircraft} filterHealth={filterHealth} />
        <HealthTable title="Boxes" {...totalStats.boxes} />
      </div>

      <div className="fase-monitor__sections">
        <Toggle title="AIRCRAFT" count={aircraftRows.length}>
          {aircraftContent}
        </Toggle>
        <Toggle title="SPARE" count={spareBoxesWithHealth.length}>
          <Spare spareBoxes={spareBoxesWithHealth} />
        </Toggle>
      </div>
    </div>
  );
}

export default FleetPage;
